import {
  AuthActions,
  BreakpointsEnum,
  ProfileInfoType,
  Selectors,
  isBreakpointDown,
  useBreakpoint,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import {NavigationContainer} from '@react-navigation/native';
import {
  StackNavigationOptions,
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import {useSelector} from 'Provider';
import envData from 'env.json';
import React, {FunctionComponent, useCallback, useEffect, useRef} from 'react';
import {AppState, NativeEventSubscription, Platform} from 'react-native';

import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {APP_BASE_URL, Routes} from 'models/Navigation';
import HomeScreen from 'screens/Home';
import LinkAccountScreen from 'screens/LinkAccount';
import OffersScreen from 'screens/Offers';
import DatesMobile from 'screens/Offers/DatesMobile';
import DestinationsMobile from 'screens/Offers/DestinationsMobile';
import HotelsAndResortsMobile from 'screens/Offers/HotelsAndResortsMobile';
import OfferFiltersMobile from 'screens/Offers/OfferFiltersMobile';
import OfferTypesMobile from 'screens/Offers/OfferTypesMobile';
import ProfileScreen from 'screens/Profile';
import SigninScreen from 'screens/Signin';
import TransferCreditsScreen from 'screens/TransferCredits';
import {rawOffersCountSelector} from 'store/offers/offers.selectors';
import {navigationReadyRef, navigationRef} from 'utils/navigation';
import AcceptTermsScreen from 'screens/AcceptTerms';
import ReservationsScreen from 'screens/Reservations';
import {useBreadSDK} from 'hooks/useBreadSDK';
import {useShouldTriggerBread} from 'hooks/useShouldTriggerBread';
import {isLocalhost, navigateToSignin} from 'utils/signin';
import Cookies from 'js-cookie';

const {API_URL} = envData;

const RootStack = createStackNavigator();
const OffersStack = createStackNavigator();
const isWeb = Platform.OS === 'web';
const MainStack = isWeb ? createStackNavigator() : createBottomTabNavigator();
const tabBarVisible = !isWeb;

const config = {
  screens: {
    [Routes.Main.StackName]: {
      screens: {
        [Routes.Experiences]: 'experiences',
        [Routes.Main.Home]: `${APP_BASE_URL}/home`,
        [Routes.Main.LinkAccount]: `${APP_BASE_URL}/link`,
        [Routes.Main.Stays]: `${APP_BASE_URL}/stays`,
        [Routes.Main.Play]: `${APP_BASE_URL}/play`,
        [Routes.Main.Profile]: `${APP_BASE_URL}/profile`,
        [Routes.Main.TransferCredits]: `${APP_BASE_URL}/transfer-credits`,
        [Routes.Main.Terms]: `${APP_BASE_URL}/accept-terms`,
        [Routes.Main.Signin]: `${APP_BASE_URL}/signin`,
        [Routes.Main.Offers]: {
          screens: {
            [Routes.Offers.Home]: `${APP_BASE_URL}/offers`,
            [Routes.Offers.Filters]: `${APP_BASE_URL}/offers/filters`,
            [Routes.Offers.Destinations]: `${APP_BASE_URL}/offers/destinations`,
            [Routes.Offers.Properties]: `${APP_BASE_URL}/offers/properties`,
            [Routes.Offers.Dates]: `${APP_BASE_URL}/offers/dates`,
            [Routes.Offers.OfferTypes]: `${APP_BASE_URL}/offers/offertypes`,
          },
        },
      },
    },
  },
};

const linking = {
  prefixes: ['https://localhost:3000', 'rewards://', `${API_URL}/rewards/`],
  config,
};

export const getForwardUrl = () => {
  if (Platform.OS === 'web') {
    const pathname = window.location.pathname;
    if (['/rewards/signin', '/rewards/signin/'].includes(pathname)) {
      return;
    }
    // If on root path, default to home
    if (pathname === '/') {
      return '/rewards/home';
    }
    return pathname + window.location.search;
  }
};

const OffersStackNav: FunctionComponent = () => {
  const screenOptions: StackNavigationOptions = {
    headerShown: false,
    ...TransitionPresets.SlideFromRightIOS,
  };

  return (
    <OffersStack.Navigator screenOptions={{animationEnabled: true}}>
      <OffersStack.Screen
        name={Routes.Offers.Home}
        component={OffersScreen}
        options={screenOptions}
      />
      <OffersStack.Screen
        name={Routes.Offers.Filters}
        component={OfferFiltersMobile}
        options={screenOptions}
      />
      <OffersStack.Screen
        name={Routes.Offers.Destinations}
        component={DestinationsMobile}
        options={screenOptions}
      />
      <OffersStack.Screen
        name={Routes.Offers.Properties}
        component={HotelsAndResortsMobile}
        options={screenOptions}
      />
      <OffersStack.Screen
        name={Routes.Offers.Dates}
        component={DatesMobile}
        options={screenOptions}
      />
      <OffersStack.Screen
        name={Routes.Offers.OfferTypes}
        component={OfferTypesMobile}
        options={screenOptions}
      />
    </OffersStack.Navigator>
  );
};

const MainStackScreen: FunctionComponent = () => {
  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector) as boolean;
  useBreadSDK(isLoggedIn, true);

  // if (!isLoggedIn && !isLocalhost()) {
  //   const forwardUrl = getForwardUrl();
  //   navigateToSignin(
  //     forwardUrl ? `forwardUrl=${encodeURIComponent(forwardUrl)}` : undefined
  //   );
  //   return null;
  // }

  return (
    <MainStack.Navigator
      initialRouteName={Routes.Main.Home}
      tabBarOptions={{
        activeTintColor: '#ad1f23',
        tabStyle: {justifyContent: 'center'},
      }}
      screenOptions={!isWeb ? {tabBarVisible: false} : {}}>
      <MainStack.Screen
        name={Routes.Main.Home}
        component={HomeScreen}
        options={{title: 'Home', tabBarVisible, headerShown: false}}
      />
      <MainStack.Screen
        name={Routes.Main.Stays}
        component={ReservationsScreen}
        options={{tabBarVisible, headerShown: false}}
      />
      <MainStack.Screen
        name={Routes.Main.Offers}
        component={OffersStackNav}
        options={{tabBarVisible, headerShown: false}}
      />
      <MainStack.Screen
        name={Routes.Main.Profile}
        component={ProfileScreen}
        options={{tabBarVisible, headerShown: false}}
      />
      <MainStack.Screen
        name={Routes.Main.Terms}
        component={AcceptTermsScreen}
        options={{tabBarVisible, headerShown: false}}
      />
      <MainStack.Screen
        name={Routes.Main.Signin}
        component={SigninScreen}
        options={{tabBarVisible, headerShown: false}}
      />
      {/**
       * commenting this out until we know what this screen is
       * and if it requires authentication or not
       */}
      {/* <MainStack.Screen
        name={Routes.Experiences}
        component={ExperiencesScreen}
        options={{tabBarVisible}}
      /> */}
    </MainStack.Navigator>
  );
};

const MOBILE_BREAKPOINT = BreakpointsEnum.MD;

export default function Navigation(): JSX.Element {
  const breakpoint = useBreakpoint();
  const isMobile = isBreakpointDown(breakpoint, MOBILE_BREAKPOINT);
  const widgetDispatch = useWidgetDispatch();
  const appState = useRef(AppState.currentState);

  const offerTotalCount = useSelector(rawOffersCountSelector);
  const {trackAdobeEvent} = useTrackEvent();
  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector) as boolean;
  const shouldUseBreadSDK = useShouldTriggerBread();
  const allProfileInfoExpired = useWidgetState(
    Selectors.getGuestProfileAllInfoExpired
  ) as boolean;

  function trackAnalyticsForOffers() {
    trackAdobeEvent({
      event: AdobeEvent.pageLoad,
      page: {name: 'rewards offers available'},
      rewards: {offerTotalCount},
    });
  }

  function trackAnalyticsForHomeScreen() {
    trackAdobeEvent({
      event: AdobeEvent.pageLoad,
      page: {name: 'my rewards home'},
    });
  }

  const handleAppStateChange = useCallback(
    nextAppState => {
      // Callback function for when app comes into focus
      const onFocus = () => {
        if (isWeb) {
          widgetDispatch(AuthActions.startAuth());
        } else {
          widgetDispatch(AuthActions.onNativeWake());
        }
      };

      // Callback function for when app goes out of focus
      const onBlur = () => {};

      // Calling the right callback function depending on the app state
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        onFocus();
      } else {
        onBlur();
      }
      appState.current = nextAppState;
    },
    [widgetDispatch]
  );

  useEffect(() => {
    const listener: NativeEventSubscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => {
      listener?.remove();
    };
  }, [handleAppStateChange]);

  useEffect(() => {
    return () => {
      navigationReadyRef.current = false;
    };
  }, []);

  const routeNameRef = useRef('');

  useEffect(() => {
    if (shouldUseBreadSDK && isLoggedIn && allProfileInfoExpired) {
      widgetDispatch(
        AuthActions.getGuestProfile({
          infoType: ProfileInfoType.allProfileInfo,
        })
      );
    }
  }, [isLoggedIn, shouldUseBreadSDK, allProfileInfoExpired]);

  // Auth guard: redirect unauthenticated users from protected routes
  useEffect(() => {
    const isLoggedInOnLoad = Cookies.get('sectoken');

    if (!isLoggedInOnLoad) {
      const currentRoute = navigationRef?.current?.getCurrentRoute();
      const protectedRoutes = [
        Routes.Main.Home,
        Routes.Main.Profile,
        Routes.Main.Stays,
        Routes.Main.Offers,
      ];

      if (currentRoute && protectedRoutes.includes(currentRoute.name)) {
        // Note: this can override auth store handleLogout
        const forwardUrl = getForwardUrl();

        navigateToSignin(
          forwardUrl
            ? `forwardUrl=${encodeURIComponent(forwardUrl)}`
            : undefined
        );
      }
    }
  }, []);

  // Vendor passthrough: redirect authenticated users to partner sites
  useEffect(() => {
    if (isLoggedIn) {
      const urlParams = new URLSearchParams(window.location.search);
      const pkey = urlParams.get('pkey');
      const trex = urlParams.get('trex');

      // Check for vendor passthrough parameters (e.g., Caesars Rewards Dining)
      if (pkey && trex) {
        const universalPAUrl = `/asp_net/universalpa.aspx?${urlParams.toString()}`;
        window.location.href = universalPAUrl;
      }
    }
  }, [isLoggedIn]);

  return (
    <NavigationContainer
      ref={navigationRef}
      documentTitle={{
        formatter: (options, route) =>
          `Caesars Rewards | ${options?.title ?? route?.name}`,
      }}
      onReady={() => {
        navigationReadyRef.current = true;
        routeNameRef.current =
          navigationRef?.current?.getCurrentRoute()?.name || '';

        if (routeNameRef.current === 'Offers') {
          trackAnalyticsForOffers();
        }
        if (routeNameRef.current === 'Home') {
          trackAnalyticsForHomeScreen();
        }
      }}
      onStateChange={state => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName =
          navigationRef?.current?.getCurrentRoute()?.name || '';

        if (previousRouteName !== currentRouteName) {
          routeNameRef.current = currentRouteName;
          if (routeNameRef.current === 'Offers') {
            trackAnalyticsForOffers();
          }
        }
      }}
      linking={linking}>
      <RootStack.Navigator
        mode="modal"
        initialRouteName={Routes.Main.StackName}
        headerMode="none">
        <RootStack.Screen
          name={Routes.Main.StackName}
          component={MainStackScreen}
        />
        {isLoggedIn ? (
          <>
            <RootStack.Screen
              name={Routes.Main.TransferCredits}
              component={TransferCreditsScreen}
            />
            <RootStack.Screen
              name={Routes.Main.LinkAccount}
              component={LinkAccountScreen}
            />
          </>
        ) : null}
      </RootStack.Navigator>
    </NavigationContainer>
  );
}
